// Custom hook for admin data fetching
import { useState, useEffect, useCallback, useRef } from "react";
import { useToast } from "@chakra-ui/react";

export const useAdminData = (fetchFunction, dependencies = []) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const toast = useToast();
  const hasShownErrorToast = useRef(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await fetchFunction();
      setData(result);
      hasShownErrorToast.current = false; // Reset error toast flag on success
    } catch (err) {
      console.error("Error fetching admin data:", err);
      console.error("Error response data:", JSON.stringify(err.response?.data));
      const errorMessage =
        err.response?.data?.detail || err.message || "Failed to load data";
      setError(errorMessage);
      // Only show toast once per error cycle to avoid spam
      if (!hasShownErrorToast.current) {
        hasShownErrorToast.current = true;
        toast({
          title: "Error",
          description: errorMessage,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } finally {
      setLoading(false);
    }
  }, [fetchFunction, toast]);

  useEffect(() => {
    fetchData();
    // Note: fetchData is intentionally not included in dependencies to prevent infinite loops
    // The fetchFunction dependency in fetchData callback ensures it updates when needed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...dependencies]);

  const refetch = useCallback(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    loading,
    error,
    refetch,
  };
};
